import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDataCompleteness, updateClientFields, type ClientCompletion } from '../../lib/refonteApi';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const OFFRES = [{ v: '', l: '—' }, { v: 'cold_calling', l: 'Cold call' }, { v: 'gtm', l: 'GTM' }, { v: 'mailing', l: 'Mailing' }, { v: 'growth', l: 'Growth' }];

// Données à compléter — un seul écran pour combler les trous des clients ACTIFS (offre,
// jours vendus, prix/jour, dates, calibrage). Édition en ligne ; le calibrage renvoie à la fiche.
const DataToCompletePage: React.FC = () => {
  const nav = useNavigate();
  const [rows, setRows] = useState<ClientCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filt, setFilt] = useState<'incomplets' | 'tous'>('incomplets');
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await getDataCompleteness()); } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id: string, fields: Record<string, any>) => {
    setSaving(id);
    setRows(rs => rs.map(r => (r.id === id ? { ...r, ...fields } : r)));
    await updateClientFields(id, fields).catch(() => {});
    await load();
    setSaving(null);
  };

  const incomplete = rows.filter(r => r.missing.length > 0);
  const shown = filt === 'incomplets' ? incomplete : rows;
  const pctGlobal = rows.length ? Math.round(((rows.length - incomplete.length) / rows.length) * 100) : 100;

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Données à compléter</h1>
        <div className="flex gap-1 bg-raise rounded-lg p-0.5">
          {([['incomplets', `À compléter (${incomplete.length})`], ['tous', `Tous (${rows.length})`]] as const).map(([v, l]) => (
            <button key={v} onClick={() => setFilt(v)} className={`text-[11.5px] font-semibold px-3 py-1 rounded-md ${filt === v ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">Les clients actifs et leurs champs manquants (contrat, jours, prix, dates, calibrage). Complète en ligne — le reste passe partout (registre, agenda, facturation, fiche).</p>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}

      {/* Barre de complétude globale */}
      {!loading && (
        <div className="bg-panel border border-line rounded-card p-4 mb-4">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-faint">Complétude des clients actifs</span>
            <span className={`font-mono text-[15px] font-bold ${pctGlobal === 100 ? 'text-st-ok' : 'text-st-warn'}`}>{pctGlobal}%</span>
          </div>
          <div className="h-2 rounded-full bg-raise overflow-hidden"><div className={`h-full ${pctGlobal === 100 ? 'bg-st-ok' : 'bg-st-warn'}`} style={{ width: `${pctGlobal}%` }} /></div>
        </div>
      )}

      {loading ? <SkeletonRows count={8} /> : shown.length === 0 ? (
        <EmptyState title={filt === 'incomplets' ? 'Tout est complet 🎉' : 'Aucun client actif'} hint={filt === 'incomplets' ? 'Aucun champ manquant sur les clients actifs.' : ''} />
      ) : (
        <div className="bg-panel border border-line rounded-card overflow-x-auto">
          <table className="w-full border-collapse min-w-[860px]">
            <thead><tr className="border-b border-line-2 font-mono text-[9.5px] uppercase tracking-widest text-faint">
              <th className="text-left py-2 px-3 font-semibold">Client</th>
              <th className="text-left py-2 px-2 font-semibold">Offre</th>
              <th className="text-left py-2 px-2 font-semibold">Jours/mois</th>
              <th className="text-left py-2 px-2 font-semibold">Prix/jour</th>
              <th className="text-left py-2 px-2 font-semibold">Début</th>
              <th className="text-left py-2 px-2 font-semibold">Fin</th>
              <th className="text-left py-2 px-2 font-semibold">Calibrage</th>
              <th className="text-left py-2 px-2 font-semibold">Manque</th>
            </tr></thead>
            <tbody className="divide-y divide-line">
              {shown.map(r => {
                const busy = saving === r.id;
                const complete = r.missing.length === 0;
                return (
                  <tr key={r.id} className={busy ? 'opacity-50' : ''}>
                    <td className="py-2 px-3">
                      <button onClick={() => nav(`/app/clients/${r.id}`)} className="text-[13px] font-semibold hover:text-hook-rose text-left">{r.name}</button>
                      {r.channel && <span className={`ml-1.5 font-mono text-[8px] px-1 rounded ${r.channel === 'gtm' ? 'text-st-ok bg-st-ok/10' : 'text-hook-rose bg-hook-rose/10'}`}>{r.channel === 'gtm' ? 'GTM' : 'CC'}</span>}
                    </td>
                    <td className="py-2 px-2">
                      <select value={r.contract_type ?? ''} onChange={e => patch(r.id, { contract_type: e.target.value || null })} className={`rounded-md border bg-panel px-1.5 py-1 text-[11.5px] ${!r.contract_type ? 'border-st-bad/50' : 'border-line-2'}`}>
                        {OFFRES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    </td>
                    <td className="py-2 px-2"><input type="number" defaultValue={r.days_per_month ?? ''} onBlur={e => { const v = e.target.value ? Number(e.target.value) : null; if (v !== r.days_per_month) patch(r.id, { days_per_month: v }); }} className={`w-16 rounded-md border bg-panel px-1.5 py-1 text-[11.5px] ${r.days_per_month == null ? 'border-st-bad/50' : 'border-line-2'}`} /></td>
                    <td className="py-2 px-2"><input type="number" defaultValue={r.price_per_day ?? ''} onBlur={e => { const v = e.target.value ? Number(e.target.value) : null; if (v !== r.price_per_day) patch(r.id, { price_per_day: v }); }} placeholder={r.pricing_model && r.pricing_model !== 'days' ? 'n/a' : '—'} className="w-20 rounded-md border border-line-2 bg-panel px-1.5 py-1 text-[11.5px]" /></td>
                    <td className="py-2 px-2"><input type="date" defaultValue={r.start_date ?? ''} onBlur={e => { const v = e.target.value || null; if (v !== r.start_date) patch(r.id, { start_date: v }); }} className={`rounded-md border bg-panel px-1 py-1 text-[11px] ${!r.start_date ? 'border-st-bad/50' : 'border-line-2'}`} /></td>
                    <td className="py-2 px-2"><input type="date" defaultValue={r.end_date ?? ''} onBlur={e => { const v = e.target.value || null; if (v !== r.end_date) patch(r.id, { end_date: v }); }} className={`rounded-md border bg-panel px-1 py-1 text-[11px] ${!r.end_date ? 'border-st-bad/50' : 'border-line-2'}`} /></td>
                    <td className="py-2 px-2">
                      {r.calibrationActive
                        ? <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-ok/10 text-st-ok">✓{r.calibrageDone ? ' fait' : ' contrat'}</span>
                        : <button onClick={() => nav(`/app/calibrage-guide?c=${r.id}`)} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-bad/10 text-st-bad hover:underline">à faire ›</button>}
                    </td>
                    <td className="py-2 px-2">
                      {complete ? <span className="font-mono text-[10px] text-st-ok">✓ complet</span> : (
                        <div className="flex flex-wrap gap-1">{r.missing.map(m => <span key={m} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn">{m}</span>)}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DataToCompletePage;
